// ViewDefinitionEditor.jsx

import React from 'react';

// Components
import ViewModalBoardFieldSection from './sections/board/ViewModalBoardFieldSection';
import ViewModalChartSection from './sections/chart/ViewModalChartSection';
import ViewModalFiltersSection from './sections/filters/ViewModalFiltersSection';
import ViewModalIncludeDescendantsSection from './sections/include/ViewModalIncludeDescendantsSection';
import ViewModalFieldsSection from './sections/properties/ViewModalFieldsSection';
import ViewModalSchedulerSection from './sections/scheduler/ViewModalSchedulerSection';
import ViewModalSortsSection from './sections/sorts/ViewModalSortsSection';
import ViewModalTimelineSection from './sections/timeline/ViewModalTimelineSection';
import ViewModalTypeCards from './types/ViewModalTypeCards';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * ViewDefinitionEditor
 *
 * Renders the shared form body for creating or editing a view definition.
 *
 * @param {string} description - The current view description.
 * @param {function} setDescription - Updates the current view description.
 * @param {string} title - The current view title.
 * @param {function} setTitle - Updates the current view title.
 * @param {function} setTitleTouched - Marks the title as edited by the user.
 * @param {array} viewOptions - Available view type options.
 * @param {string} type - Selected view type.
 * @param {string} selectedOptionKey - Selected view-type option key (includes timeline presets).
 * @param {function} setType - Updates the selected view type / preset.
 * @param {object} theme - Active theme values.
 * @param {boolean} showIncludeDescendants - Whether the include-descendants toggle should render.
 * @param {string} includeDescendantsLabel - Toggle label for descendant inclusion.
 * @param {boolean} includeDescendants - Current include-descendants value.
 * @param {function} setIncludeDescendants - Updates include-descendants state.
 * @param {boolean} hasEnumFields - Whether enum-backed board grouping is available.
 * @param {string} boardField - Selected board field key.
 * @param {function} setBoardField - Updates the selected board field key.
 * @param {array} enumFields - Enum-capable fields for board grouping.
 * @param {function} setBoardEnumOptions - Updates board enum options.
 * @param {array} boardFieldOptions - Options for board field enum ordering.
 * @param {string} timelineField - Selected timeline field key.
 * @param {function} setTimelineField - Updates timeline field key.
 * @param {string} timelineRowMode - Current timeline row mode.
 * @param {function} setTimelineRowMode - Updates timeline row mode.
 * @param {string} timelineGroupField - Selected grouped timeline field key.
 * @param {function} setTimelineGroupField - Updates grouped timeline field key.
 * @param {boolean} showUnscheduled - Whether unscheduled timeline rows should render.
 * @param {function} setShowUnscheduled - Updates unscheduled timeline visibility.
 * @param {string} preset - Timeline preset ('timeline' | 'gantt').
 * @param {function} setPreset - Updates timeline preset.
 * @param {boolean} showHierarchy - Whether hierarchy rows should render.
 * @param {function} setShowHierarchy - Updates hierarchy visibility.
 * @param {boolean} showDependencies - Whether dependency lines should render.
 * @param {function} setShowDependencies - Updates dependency visibility.
 * @param {boolean} allowScheduleEdit - Whether schedule drag-edit is allowed.
 * @param {function} setAllowScheduleEdit - Updates schedule-edit permission.
 * @param {array} timelineTemporalFields - Timeline-compatible date fields.
 * @param {array} timelineGroupFields - Timeline-compatible grouping fields.
 * @param {object} chartConfig - Current chart configuration.
 * @param {function} setChartConfig - Updates chart configuration.
 * @param {array} chartDimensionOptions - Dimension options for charts.
 * @param {array} chartMeasureOptions - Measure options for charts.
 * @param {string} scheduledType - Selected scheduled node type.
 * @param {function} setScheduledType - Updates the scheduled node type.
 * @param {array} scheduledTypeOptions - Available scheduled type options.
 * @param {string} scheduledField - Selected scheduled field key.
 * @param {function} setScheduledField - Updates the scheduled field key.
 * @param {array} scheduledFieldOptions - Available scheduled date-time range fields.
 * @param {string} slotIncrementMinutes - Scheduler slot increment in minutes.
 * @param {function} setSlotIncrementMinutes - Updates the slot increment.
 * @param {array} filters - Current filter rows.
 * @param {function} setFilters - Updates current filter rows.
 * @param {array} filterableFields - Fields that can be filtered.
 * @param {Map} filterFieldMap - Metadata map for filter fields.
 * @param {array} filterFieldOptions - Filter/sort select options.
 * @param {array} sorts - Current sort rows.
 * @param {function} setSorts - Updates current sort rows.
 * @param {boolean} showPropertiesSection - Whether the properties section should render.
 * @param {array} properties - Currently selected properties.
 * @param {function} setProperties - Updates selected properties.
 * @param {array} propertyFieldOptions - Available field options.
 * @param {array} scheduledFilters - Current scheduled-side filter rows.
 * @param {function} setScheduledFilters - Updates scheduled-side filter rows.
 * @param {array} scheduledFilterableFields - Scheduled-side filterable fields.
 * @param {Map} scheduledFilterFieldMap - Scheduled-side field metadata map.
 * @param {array} scheduledFilterFieldOptions - Scheduled-side filter select options.
 * @param {array} scheduledProperties - Currently selected scheduled card properties.
 * @param {function} setScheduledProperties - Updates scheduled card properties.
 * @param {array} scheduledPropertyFieldOptions - Scheduled-side property field options.
 * @param {string} width - The width of the form.
 *
 * @returns {ReactNode} The shared view definition editor body.
 */
const ViewDefinitionEditor = ({
    description,
    setDescription,
    title,
    setTitle,
    setTitleTouched,
    viewOptions,
    type,
    selectedOptionKey = '',
    setType,
    theme,
    showIncludeDescendants,
    includeDescendantsLabel,
    includeDescendants,
    setIncludeDescendants,
    hasEnumFields,
    boardField,
    setBoardField,
    boardLayout = 'kanban',
    setBoardLayout,
    viewGroupId = '',
    setViewGroupId,
    viewGroupOptions = [],
    showViewGroupField = false,
    enumFields,
    setBoardEnumOptions,
    boardFieldOptions,
    timelineField,
    setTimelineField,
    timelineRowMode,
    setTimelineRowMode,
    timelineGroupField,
    setTimelineGroupField,
    showUnscheduled,
    setShowUnscheduled,
    preset = 'timeline',
    setPreset,
    showHierarchy = false,
    setShowHierarchy,
    showDependencies = false,
    setShowDependencies,
    allowScheduleEdit = false,
    setAllowScheduleEdit,
    timelineTemporalFields,
    timelineGroupFields,
    chartConfig,
    setChartConfig,
    chartDimensionOptions,
    chartMeasureOptions,
    scheduledType,
    setScheduledType,
    scheduledTypeOptions,
    scheduledField,
    setScheduledField,
    scheduledFieldOptions,
    slotIncrementMinutes,
    setSlotIncrementMinutes,
    filters,
    setFilters,
    filterableFields,
    filterFieldMap,
    filterFieldOptions,
    sorts,
    setSorts,
    showPropertiesSection,
    properties,
    setProperties,
    propertyFieldOptions,
    scheduledFilters,
    setScheduledFilters,
    scheduledFilterableFields,
    scheduledFilterFieldMap,
    scheduledFilterFieldOptions,
    scheduledProperties,
    setScheduledProperties,
    scheduledPropertyFieldOptions,
    width = '100%',
}) => {

  // ----- RENDER ----- //

  return (

        <ModalForm width={width}>

            <ModalField>

                <ModalLabel label="Title" />
                <ModalHint hint="Give your view a short, descriptive title." />

                <TextInput
                  value={title}
                  placeholder="Add a view title..."
                  maxLength={100}
                  autoFocus
                  autoSelect
                  onChange={(event) => {
                        setTitleTouched(true);
                        setTitle(event.target.value);
                    }}
                />

            </ModalField>

            <ModalField>

                <ModalLabel label="Description" />
                <ModalHint hint="Add more contextual information for tooltips and forms." />

                <TextInput
                  value={description}
                  placeholder="Optional short description for this view..."
                  maxLength={240}
                  multiline
                  expandable
                  onChange={(event) => setDescription(event.target.value)}
                />

            </ModalField>

            <ViewModalTypeCards
              options={viewOptions}
              selectedKey={selectedOptionKey || type}
              theme={theme}
              onSelect={setType}
            />

            {showViewGroupField ? (
              <ModalField>
                <ModalLabel label="View Group" />
                <ModalHint hint="Optional. Groups this view in the collection switcher." />
                <SelectInput
                  value={viewGroupId || ''}
                  options={viewGroupOptions}
                  placeholder="Unsorted"
                  onValueChange={(nextValue) => setViewGroupId?.(nextValue || '')}
                />
              </ModalField>
            ) : null}

            <ViewModalIncludeDescendantsSection
              isVisible={showIncludeDescendants}
              label={includeDescendantsLabel}
              value={includeDescendants}
              onToggle={() => setIncludeDescendants((prev) => !prev)}
            />

            <ViewModalBoardFieldSection
              isVisible={type === 'BOARD' && hasEnumFields}
              boardField={boardField}
              setBoardField={setBoardField}
              boardLayout={boardLayout}
              setBoardLayout={setBoardLayout}
              enumFields={enumFields}
              setBoardEnumOptions={setBoardEnumOptions}
              boardFieldOptions={boardFieldOptions}
            />

            <ViewModalTimelineSection
              isVisible={type === 'TIMELINE'}
              timelineField={timelineField}
              setTimelineField={setTimelineField}
              timelineRowMode={timelineRowMode}
              setTimelineRowMode={setTimelineRowMode}
              timelineGroupField={timelineGroupField}
              setTimelineGroupField={setTimelineGroupField}
              showUnscheduled={showUnscheduled}
              setShowUnscheduled={setShowUnscheduled}
              preset={preset}
              setPreset={setPreset}
              showHierarchy={showHierarchy}
              setShowHierarchy={setShowHierarchy}
              showDependencies={showDependencies}
              setShowDependencies={setShowDependencies}
              allowScheduleEdit={allowScheduleEdit}
              setAllowScheduleEdit={setAllowScheduleEdit}
              timelineTemporalFields={timelineTemporalFields}
              timelineGroupFields={timelineGroupFields}
            />

            <ViewModalChartSection
              isVisible={type === 'CHART'}
              chartConfig={chartConfig}
              setChartConfig={setChartConfig}
              chartDimensionOptions={chartDimensionOptions}
              chartMeasureOptions={chartMeasureOptions}
            />

            <ViewModalSchedulerSection
              isVisible={type === 'SCHEDULER'}
              scheduledType={scheduledType}
              setScheduledType={setScheduledType}
              scheduledTypeOptions={scheduledTypeOptions}
              scheduledField={scheduledField}
              setScheduledField={setScheduledField}
              scheduledFieldOptions={scheduledFieldOptions}
              slotIncrementMinutes={slotIncrementMinutes}
              setSlotIncrementMinutes={setSlotIncrementMinutes}
            />

            <ViewModalFiltersSection
              filters={filters}
              setFilters={setFilters}
              filterableFields={filterableFields}
              filterFieldMap={filterFieldMap}
              fieldOptions={filterFieldOptions}
              theme={theme}
              label={type === 'SCHEDULER' ? 'Resource Filters' : 'Filters'}
              hint={type === 'SCHEDULER'
                    ? 'These filters decide which resource records appear as scheduler columns.'
                    : 'Filters allow for refining the data according to specific criteria.'}
            />

            <ViewModalSortsSection
              sorts={sorts}
              setSorts={setSorts}
              filterableFields={filterableFields}
              fieldOptions={filterFieldOptions}
              label={type === 'SCHEDULER' ? 'Resource Sorts' : 'Sorts'}
              hint={type === 'SCHEDULER'
                    ? 'These sorts determine the order of resource columns in the schedule.'
                    : 'Sorts allow for ordering the data according to specific criteria.'}
            />

            <ViewModalFiltersSection
              isVisible={type === 'SCHEDULER'}
              filters={scheduledFilters}
              setFilters={setScheduledFilters}
              filterableFields={scheduledFilterableFields}
              filterFieldMap={scheduledFilterFieldMap}
              fieldOptions={scheduledFilterFieldOptions}
              theme={theme}
              label="Scheduled Filters"
              hint="These filters refine which scheduled records render as cards in the schedule."
              emptyHint="No scheduled filters added yet."
              addButtonLabel="+ Add Scheduled Filter"
            />

            <ViewModalFieldsSection
              isVisible={showPropertiesSection}
              properties={properties}
              setProperties={setProperties}
              fieldOptions={propertyFieldOptions}
            />

            <ViewModalFieldsSection
              isVisible={type === 'SCHEDULER'}
              properties={scheduledProperties}
              setProperties={setScheduledProperties}
              fieldOptions={scheduledPropertyFieldOptions}
              label="Card Properties"
              hint="These fields will be shown on scheduled cards inside the scheduler."
            />

        </ModalForm>
    );
};

// Dev Tools
ViewDefinitionEditor.displayName = 'ViewDefinitionEditor';

export default ViewDefinitionEditor;
